<template>
  <v-navigation-drawer
    app
    permanent
    :dark="isImpersonateEnterprise"
    :color="navColor"
  >
    <template #prepend>
      <div class="d-flex justify-center ma-2 pa-1">
        <v-img
          v-if="hasFirmImage"
          :key="getFirmImageId"
          :src="getFirmImageUrl"
          :alt="selectedFirm.name + ' logo'"
        />
        <LogoProvider v-else :height="40" :width="129" :colors="logoColors" />
      </div>
    </template>
    <template v-if="isImpersonateEnterprise">
      <v-list-item class="px-2">
        <v-list-item-content>
          <div class="d-flex align-center rounded pa-2 techBlue">
            <div class="text-caption white--text font-weight-bold">
              {{ enterpriseName }}
            </div>
            <v-spacer />
            <ExitEnterpriseButtonDialog @confirm="handleClose" />
          </div>
        </v-list-item-content>
      </v-list-item>
    </template>
    <v-list dense nav>
      <template v-for="route in routes">
        <template v-if="route.subFolders">
          <v-list-group
            :key="'route ' + route.name"
            v-model="route.active"
            :color="listGroupColor"
          >
            <template #activator>
              <v-list-item-title>{{ route.title }}</v-list-item-title>
            </template>
            <template #prependIcon>
              <v-icon>{{ route.icon }}</v-icon>
            </template>
            <v-list-item
              v-for="(subFolder, index) in route.subFolders"
              :key="index"
              :to="{ name: subFolder.name, params: subFolder.params }"
            >
              <v-list-item-title>{{ subFolder.title }}</v-list-item-title>
            </v-list-item>
          </v-list-group>
        </template>
        <template v-else>
          <v-list-item
            :key="'route ' + route.name"
            :to="{ name: route.name, params: route.params }"
          >
            <v-list-item-icon>
              <v-icon>{{ route.icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-title>{{ route.title }}</v-list-item-title>
          </v-list-item>
        </template>
      </template>
    </v-list>
    <template #append>
      <v-divider />
      <v-list dense nav>
        <v-list-item v-if="hasFirmImage">
          <v-list-item-content>
            <LogoProvider :height="40" :width="129" :colors="logoColors" />
          </v-list-item-content>
        </v-list-item>
        <v-list-item>
          <v-list-item-content>
            <v-list-item-title class="d-flex align-center">
              {{ userFullName }}
              <v-spacer></v-spacer>
              <v-btn icon :to="{ name: 'settings' }">
                <v-icon> mdi-cog </v-icon>
              </v-btn>
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item @click="logout()">
          <v-list-item-icon>
            <v-icon>mdi-logout</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>Logout</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </template>
  </v-navigation-drawer>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { FirmService, EnterpriseService, FirmImageService } from "@/services";
import { showErrorSnackbar, showSuccessSnackbar } from "@/utilities/snackbar";
import { PricingTierModule } from "@/utilities/enums";
import ExitEnterpriseButtonDialog from "@/components/dialogs/ExitEnterpriseButtonDialog.vue";
import LogoProvider from "@/components/global/LogoProvider.vue";
import { useBrand } from "@/composables/useBrand";

export default {
  name: "TheNavbar",
  components: {
    ExitEnterpriseButtonDialog,
    LogoProvider,
  },
  setup() {
    const { isDsr } = useBrand();
    return { isDsr };
  },
  data: () => ({
    enterprise: {},
    allEnterpriseFlat: [],
    referenceFirmId: undefined,
    referenceEnterpriseId: undefined,
  }),
  computed: {
    ...mapGetters({
      userEnterpriseId: "user/getUserEnterpriseId",
      userFirmId: "user/getUserFirmId",
      isRoot: "user/isRoot",
      isFirmAdmin: "user/isFirmAdmin",
      userFullName: "user/userFullName",
      getReferenceFirmId: "user/getReferenceFirmId",
      getReferenceEnterpriseId: "user/getReferenceEnterpriseId",
      isImpersonateEnterprise: "user/isImpersonateEnterprise",
      userRootFirm: "user/getUserRootFirm",
      getFirmImageUrl: "firmImage/getFirmImageUrl",
      getFirmImageId: "firmImage/getFirmImageId",
      hasFirmImage: "firmImage/hasFirmImage",
    }),
    logoColors() {
      return this.isImpersonateEnterprise
        ? ["#1a67f8", "#ffffff"]
        : ["#1a67f8", "#050d21"];
    },
    navColor() {
      return this.isImpersonateEnterprise ? "techBlueDark" : "white";
    },
    listGroupColor() {
      return this.isImpersonateEnterprise ? "techPurple" : "techBlue";
    },
    enterpriseBaseRoutes() {
      let routes = [
        {
          name: "balances.index",
          params: {},
          icon: "mdi-scale-balance",
          title: this.$t("balances"),
        },
        {
          name: "business-plans.index",
          params: {},
          icon: "mdi-chart-box-outline",
          title: this.$t("business-plans"),
        },
        {
          name: "esg.eligibility-tests.index",
          params: {},
          icon: "mdi-leaf",
          title: this.$t("esg.default"),
        },
        {
          name: "dsr.index",
          params: {},
          icon: "mdi-book-account",
          title: this.$t("dsr.default"),
        },
        {
          name: undefined,
          params: {},
          icon: "mdi-folder-multiple",
          title: this.$t("documents"),
          subFolders: [
            {
              name: "documents.enterprise",
              params: {},
              icon: "mdi-folder-open",
              title: this.$t("enterprise-documents"),
              disabled: false,
            },
            {
              name: "documents.project",
              params: {},
              icon: "mdi-folder-open",
              title: this.$t("project-documents"),
              disabled: false,
            },
            {
              name: "documents.other",
              params: {},
              icon: "mdi-folder-open",
              title: this.$t("other-documents"),
              disabled: false,
            },
          ],
          disabled: false,
        },
        {
          name: "enterprise.info",
          params: {},
          icon: "mdi-information-box-outline",
          title: this.$t("enterprise-info"),
          disabled: false,
        },
      ];

      if (this.isFirmAdmin) {
        routes.push({
          name: "enterprise.users",
          params: {},
          icon: "mdi-account-group",
          title: this.$t("users"),
        });
      }

      const MODULE_ROUTES = {
        [PricingTierModule.DBP]: ["balances.index", "business-plans.index"],
        [PricingTierModule.DASHBOARD_ENTERPRISE_CRISIS]: ["crisis-dashboard"],
        [PricingTierModule.TAXONOMY]: ["esg.eligibility-tests.index"],
        [PricingTierModule.DSR]: ["dsr.index"],
        [PricingTierModule.CASHFLOWS]: ["invoices"],
      };

      const disabledRouteNames = new Set(
        this.userRootFirm?.subscriptionData.modules
          .filter((m) => !m.enabled)
          .flatMap((m) => MODULE_ROUTES[m.id] ?? []) ?? []
      );

      return routes.filter((r) => !disabledRouteNames.has(r.name));
    },
    firmBaseRoutes() {
      const isAdmin = this.isFirmAdmin;
      const base = [
        {
          name: "firm.enterprises.index",
          params: {},
          icon: "mdi-domain",
          title: this.$t("enterprises"),
          disabled: false,
        },
      ];
      if (isAdmin) {
        base.push({
          name: "firm.users",
          params: {},
          icon: "mdi-account-group",
          title: this.$t("users"),
          disabled: true,
        });
        base.push({
          name: "firm.customization",
          params: {},
          icon: "mdi-palette",
          title: this.$t("customization"),
          disabled: false,
        });
      }
      return base;
    },
    rootBaseRoutes() {
      return [
        {
          name: "admin.firms",
          params: {},
          icon: "mdi-account-group",
          title: this.$t("firms"),
          disabled: false,
        },
        {
          name: "admin.esg.upload",
          params: {},
          icon: "mdi-leaf",
          title: this.$t("esg.default"),
          disabled: false,
        },
        {
          name: "admin.pricing-tiers",
          params: {},
          icon: "mdi-playlist-star",
          title: this.$t("pricing-tiers"),
          disabled: false,
        },
        {
          name: "admin.root-users",
          params: {},
          icon: "mdi-shield-account",
          title: this.$t("root-users"),
          disabled: false,
        },
        {
          name: "admin.async-migration-jobs",
          params: {},
          icon: "mdi-cog",
          title: "Migrations",
          disabled: false,
        },
      ];
    },
    enterpriseName() {
      if (this.selectedEnterprise == null) return "";
      const name = this.selectedEnterprise.name ?? "";
      const maxLength = 25;
      return name.length > maxLength
        ? name.substring(0, maxLength) + "..."
        : name;
    },
    selectedEnterprise() {
      if (this.enterprise?.id) {
        return this.enterprise;
      } else if (this.getReferenceEnterpriseId) {
        return this.allEnterpriseFlat.find((element) => {
          return element.id === this.getReferenceEnterpriseId;
        });
      }
      return {};
    },
    selectedFirm() {
      return this.userRootFirm ?? {};
    },
    routes() {
      if (this.userFirmId) {
        return this.getReferenceEnterpriseId
          ? [...this.enterpriseBaseRoutes]
          : [...this.firmBaseRoutes];
      } else if (this.userEnterpriseId) {
        return [...this.enterpriseBaseRoutes];
      } else if (this.isRoot) {
        return [...this.rootBaseRoutes];
      }
      return [];
    },
  },
  watch: {
    referenceFirmId(newValue) {
      this.setReferenceFirmId(newValue);
      if (this.referenceFirmId) {
        this.getAllEnterpriseFlat();
      }
    },
    referenceEnterpriseId(newValue) {
      this.setReferenceEnterpriseId(newValue);
      if (newValue) {
        this.getEnterprise(newValue);
      }
      if (this.referenceFirmId) {
        this.getAllEnterpriseFlat();
      }
    },
    getReferenceFirmId(newValue) {
      this.referenceFirmId = newValue;
    },
    getReferenceEnterpriseId(newValue) {
      this.referenceEnterpriseId = newValue;
    },
  },
  mounted() {
    this.referenceFirmId = this.getReferenceFirmId;
    if (this.getReferenceEnterpriseId) {
      this.referenceEnterpriseId = this.getReferenceEnterpriseId;
    }
    this.fetchFirmImage(this.referenceFirmId);
  },
  methods: {
    ...mapActions({
      resetState: "user/resetState",
      setReferenceFirmId: "user/setReferenceFirmId",
      setReferenceEnterpriseId: "user/setReferenceEnterpriseId",
      setIsEnterpriseMicro: "user/setIsEnterpriseMicro",
      setActiveLocale: "user/setActiveLocale",
      setFirmImage: "firmImage/setFirmImage",
      setFirmImageId: "firmImage/setFirmImageId",
    }),
    logout() {
      try {
        const wasDsr = this.isDsr;
        this.resetState();
        const route = { name: "auth.login" };
        if (wasDsr) route.query = { module: "dsr" };
        this.$router.push(route);
        showSuccessSnackbar(200, this.$t("logout-done-correctly"));
      } catch (ex) {
        showErrorSnackbar(undefined, ex);
      }
    },
    async fetchFirmImage(firmId) {
      try {
        const data = await FirmImageService.getSignedUrlFromFirm(firmId);
        this.setFirmImage(data.preSignedUrl);
        this.setFirmImageId(data.id);
      } catch (error) {
        this.setFirmImage(null);
      }
    },
    async getAllEnterpriseFlat() {
      try {
        this.allEnterpriseFlat = await FirmService.getEnterprises(
          this.referenceFirmId
        );
      } catch (ex) {
        showErrorSnackbar(undefined, this.$t("error-retrieving-enterprises"));
      }
    },
    async getEnterprise(id) {
      this.enterprise = await EnterpriseService.get(id);
      this.setIsEnterpriseMicro(this.enterprise.isMicroEnterprise);
    },
    handleClose: function () {
      this.setReferenceEnterpriseId(null);
      this.setIsEnterpriseMicro(null);
      this.$router.push({ name: "firm.enterprises.index" });
      showSuccessSnackbar(200, this.$t("back-to-administration-notice"));
    },
  },
};
</script>
